/**
 * VehicleDerivation
 *
 * Shared HR-facing "how this car benefit was calculated" breakdown, per IRAS
 * Appendix 8A (d5). Used by both the Employee BIK statement and the Motor
 * Vehicle BIK tab so the derivation is rendered identically everywhere.
 *
 * Props (decoupled from any particular API field naming):
 *   - derivation: the `derivation` object from compute_car_benefit()
 *   - annual:     annual car benefit (before proration)
 *   - prorated:   prorated taxable benefit for the year
 *   - daysAssigned: days assigned in the year
 *   - year:       tax year (for days-in-year denominator)
 *
 * Design System: Admin Theme (Red)
 */

import React from 'react';

const formatMoney = (value) =>
  `$${parseFloat(value || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;

const daysInYear = (y) => ((y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? 366 : 365);

const Money = ({ value, className = '' }) => (
  <span className={`tabular-nums ${className}`}>{formatMoney(value)}</span>
);

// A single "op label .......... $value" calculation line.
const CalcLine = ({ label, value, op, strong }) => (
  <div className={`flex items-center justify-between gap-3 ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
    <span className="flex items-center gap-1">
      {op && <span className="text-gray-400 w-3 inline-block text-center">{op}</span>}
      {label}
    </span>
    <Money value={value} />
  </div>
);

const VehicleDerivation = ({ derivation, annual, prorated, daysAssigned, year }) => {
  const d = derivation || {};
  const cap = d.capital;
  const run = d.running || {};
  const days = daysInYear(year);

  const runningLines = [
    ['Road tax', run.road_tax],
    ['Insurance', run.insurance],
    ['Maintenance', run.maintenance],
    ['Petrol', run.petrol],
    ['Lease (\u00d7 12)', run.lease_annual],
  ].filter(([, v]) => parseFloat(v || 0) > 0);

  return (
    <div className="rounded-lg bg-gray-50 p-3 text-xs space-y-3">
      {/* Capital component */}
      <div className="space-y-1">
        <p className="font-medium text-gray-700">Capital component / year</p>
        {cap ? (
          <>
            {cap.car_cost != null && <CalcLine label="Car cost" value={cap.car_cost} />}
            {cap.pqp_paid != null && <CalcLine label="COE renewal (PQP)" value={cap.pqp_paid} op="+" />}
            {cap.parf_rebate != null && parseFloat(cap.parf_rebate) > 0 && (
              <CalcLine label="PARF rebate" value={cap.parf_rebate} op={cap.pqp_paid != null ? '+' : '\u2212'} />
            )}
            <div className="flex items-center justify-between gap-3 text-gray-500">
              <span className="flex items-center gap-1">
                <span className="text-gray-400 w-3 inline-block text-center">{'\u00f7'}</span>
                {cap.divisor_label}
              </span>
            </div>
            <CalcLine label="Capital / year" value={cap.value} strong />
          </>
        ) : (
          <p className="text-amber-600">
            {d.note || 'Capital component not available (using approximation).'}
          </p>
        )}
      </div>

      {/* Running & maintenance */}
      <div className="space-y-1 border-t border-gray-200 pt-2">
        <p className="font-medium text-gray-700">Running &amp; maintenance / year</p>
        {runningLines.length > 0 ? (
          runningLines.map(([label, value]) => <CalcLine key={label} label={label} value={value} op="+" />)
        ) : (
          <p className="text-gray-400">No running costs recorded.</p>
        )}
        <CalcLine label="Running total" value={d.running_total} strong />
      </div>

      {/* Annual + proration */}
      <div className="space-y-1 border-t border-gray-200 pt-2">
        <div className="flex items-center justify-between gap-3 text-gray-600">
          <span>(Capital + Running) &times; 3/7</span>
          <Money value={annual} />
        </div>
        <div className="flex items-center justify-between gap-3 text-gray-600">
          <span>&times; {daysAssigned}/{days} days assigned</span>
          <Money value={prorated} className="font-semibold text-gray-900" />
        </div>
      </div>
    </div>
  );
};

export default VehicleDerivation;
